import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApolloError, useApolloClient, useQuery } from "@apollo/client";
import { ExternalServiceHistoryType, Query, QueryGetVehicleArgs, VehicleType } from "../generated/nest-graphql";
import { GET_VEHICLE_FOR_HISTORY } from "../graphql/queries/getVehicleForHistory";

export type VehicleHistory = {
  vehicle: VehicleType | null;
  carfax: ExternalServiceHistoryType[];
  carfaxUpdatedAt: Date | null;
  loading: boolean;
  error?: Error;
  /** The record no longer exists, so retrying cannot help. */
  missing: boolean;
  refetch: () => Promise<unknown>;
  /** When a refresh was accepted. Carfax answers in the background, so this is a request, not a result. */
  requestedAt: Date | null;
  refreshing: boolean;
  refreshError: boolean;
  refreshCarfax: () => Promise<void>;
};

/** The backend fetches Carfax after it replies, so the stored copy is read again once it has had time. */
const RECHECK_DELAY_MS = 10000;
const RECHECK_ATTEMPTS = 3;

/** getVehicle is non-nullable, so a deleted vehicle arrives as this error rather than as null. */
const isMissingVehicle = (error?: ApolloError) =>
  !!error?.graphQLErrors?.some((graphQLError) =>
    graphQLError.message.includes("Cannot return null for non-nullable field Query.getVehicle")
  );

/** Newest Carfax record doubles as "when we last heard from them". */
const latestUpdatedAt = (records: ExternalServiceHistoryType[]): Date | null => {
  const times = records.map((record) => (record?.updatedAt ? new Date(record.updatedAt).getTime() : 0));
  const newest = Math.max(0, ...times);
  return newest ? new Date(newest) : null;
};

export const useVehicleHistory = (vehicleId?: string | null): VehicleHistory => {
  const client = useApolloClient();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [requestedAt, setRequestedAt] = useState<Date | null>(null);
  const recheckTimer = useRef<number>();
  // The refresh resolves after an await, so it must not schedule or set state once the view is gone.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      window.clearTimeout(recheckTimer.current);
    };
  }, []);

  // A refresh belongs to one vehicle: a different one starts clean, and an old request or recheck is ignored.
  const currentVehicleId = useRef(vehicleId);
  const requestCount = useRef(0);
  const carfaxUpdatedAtRef = useRef<Date | null>(null);
  useEffect(() => {
    currentVehicleId.current = vehicleId;
    window.clearTimeout(recheckTimer.current);
    setRequestedAt(null);
    setRefreshError(false);
    setRefreshing(false);
  }, [vehicleId]);

  // Opening the job refreshes Carfax in the background, so a cached copy from earlier in the session is stale.
  const { data, loading, error, refetch } = useQuery<Query, QueryGetVehicleArgs>(GET_VEHICLE_FOR_HISTORY, {
    variables: { id: vehicleId as string, skipUpdate: true },
    skip: !vehicleId,
    fetchPolicy: "cache-and-network",
  });

  // A one-off query, not a refetch, so the watched query keeps skipUpdate: true. The
  // result lands on the same normalized Vehicle, which updates the watched query.
  const refreshCarfax = useCallback(async () => {
    if (!vehicleId) return;
    // Each refresh owns its re-checks: a newer one, another vehicle or unmounting makes this one's stale.
    const request = ++requestCount.current;
    const stillCurrent = () =>
      mounted.current && currentVehicleId.current === vehicleId && requestCount.current === request;
    // Compared with what was stored before the request, not the phone's clock, which may not match the server's.
    const storedBefore = carfaxUpdatedAtRef.current?.getTime() ?? 0;
    setRefreshing(true);
    setRefreshError(false);
    try {
      await client.query<Query, QueryGetVehicleArgs>({
        query: GET_VEHICLE_FOR_HISTORY,
        variables: { id: vehicleId, skipUpdate: false },
        fetchPolicy: "network-only",
      });
      if (!stillCurrent()) return;
      setRequestedAt(new Date());
      window.clearTimeout(recheckTimer.current);
      // Carfax answers in its own time, so the stored copy is re-read until it holds records newer than before
      // the request, or the checks run out; only then does the "requested" notice go.
      const recheck = (attempt: number) => {
        recheckTimer.current = window.setTimeout(() => {
          client
            .query<Query, QueryGetVehicleArgs>({
              query: GET_VEHICLE_FOR_HISTORY,
              variables: { id: vehicleId, skipUpdate: true },
              fetchPolicy: "network-only",
            })
            .then((result) => {
              const records = (result.data?.getVehicle?.externalServiceHistory ?? []) as ExternalServiceHistoryType[];
              const updatedAt = latestUpdatedAt(records.filter(Boolean));
              return !!updatedAt && updatedAt.getTime() > storedBefore;
            })
            .catch(() => false)
            .then((arrived) => {
              if (!stillCurrent()) return;
              if (arrived || attempt >= RECHECK_ATTEMPTS) setRequestedAt(null);
              else recheck(attempt + 1);
            });
        }, RECHECK_DELAY_MS);
      };
      recheck(1);
    } catch {
      if (stillCurrent()) setRefreshError(true);
    } finally {
      if (stillCurrent()) setRefreshing(false);
    }
  }, [client, vehicleId]);

  const vehicle = (data?.getVehicle ?? null) as VehicleType | null;
  const carfax = useMemo(
    () => (vehicle?.externalServiceHistory ?? []).filter(Boolean) as ExternalServiceHistoryType[],
    [vehicle]
  );
  const carfaxUpdatedAt = useMemo(() => latestUpdatedAt(carfax), [carfax]);
  carfaxUpdatedAtRef.current = carfaxUpdatedAt;

  return {
    vehicle,
    carfax,
    carfaxUpdatedAt,
    // A background refresh of data already on screen is neither a loading state nor a failure.
    loading: loading && !data,
    error: data ? undefined : (error as Error | undefined),
    missing: !data && isMissingVehicle(error),
    refetch,
    requestedAt,
    refreshing,
    refreshError,
    refreshCarfax,
  };
};
